import * as React from 'react'
import { Calendar, CalendarProps, Components, EventPropGetter } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { useTranslation } from 'react-i18next'
import { Size } from '@ocean/types/dist/appearance'
import { common, controls } from '@ocean/ui'
import { LoggerUtils, StyledUtils, DateUtils } from '@ocean/utils'
import { BigCalendarWrapper } from './BigCalendar.styled'
import { Toolbar, ReferenceList } from './components'
import { useCalendarGaps } from './hooks'
import {
  BaseEvent as Event,
  Resource,
  PlannerProps,
  OnEventDropProps,
  OnEventResizeProps,
  OnSelectSlotProps,
  OnSelectEventProps,
} from './types'
import BigCalendarUtils from './utils/BigCalendarUtils'

const { Spacer } = common
const { Button, ButtonVariant } = controls

// DnD Example
// https://github.com/jquense/react-big-calendar/blob/master/examples/demos/dnd.js
// withDragAndDropProps - custom props for DragAndDropCalendar
const DragAndDropCalendar = withDragAndDrop<Event, Resource>(
  Calendar as React.ComponentType<CalendarProps<Event, Resource>>
)

const BigCalendar: React.FC<PlannerProps<Event, Resource>> = props => {
  const {
    events,
    resources,
    defaultDate,
    resourceIdAccessor = 'resourceId',
    resourceTitleAccessor = 'resourceTitle',
    onEventResize,
    onEventDrop,
    selectable,
    onSelectSlot,
    draggableAccessor,
    onView,
    onSelectEvent,
    shutdownDurationGap = BigCalendarUtils.DEFAULT_SHUTDOWN_DURATION,
    launchDurationGap = BigCalendarUtils.DEFAULT_LAUNCH_DURATION,
    getNow,
    onRangeChange,
    onRefresh,
    dataTest = 'Planner',
  } = props
  const { t } = useTranslation()

  const MACHINE_TOTAL_REBOOT_DURATION = shutdownDurationGap + launchDurationGap

  const COMPONENTS: Components<Event, Resource> = {
    toolbar: props => {
      return (
        <>
          <Toolbar {...props} />
          <ReferenceList refs={BigCalendarUtils.getReferences()} />
        </>
      )
    },
  }

  const {
    getOverlappedEvents,
    resetHiddenGapsEvent,
    getNextAccessibleArea,
    slotPropGetter,
    handleDragStart,
    handleDragOver,
  } = useCalendarGaps(events, shutdownDurationGap, launchDurationGap)

  const eventStyleGetter: EventPropGetter<Event> = event => {
    const { inactive, currentBooking } = event
    const now = getNow ? getNow().getTime() : undefined
    const eventStatus = BigCalendarUtils.getEventStatus(event, now)
    const statusClassName =
      typeof eventStatus === 'number' ? BigCalendarUtils.STATUS_CLASSNAMES[eventStatus] : ''
    const draggable = typeof draggableAccessor === 'function' ? draggableAccessor(event) : true
    const draggableClassName = draggable ? 'draggable' : ''
    const currentClassName = currentBooking ? BigCalendarUtils.CURRENT_BOOKING_CLASSNAME : ''
    const inactiveClassName = inactive ? BigCalendarUtils.INACTIVE_CLASSNAME : ''
    const className = StyledUtils.getClassNames([
      statusClassName,
      draggableClassName,
      currentClassName,
      inactiveClassName,
    ])

    return { className }
  }

  function checkValidStartDate(startDate: Date): boolean {
    return BigCalendarUtils.checkValidStartDate(startDate, getNow, launchDurationGap)
  }

  // Move event
  function handleEventDrop(args: OnEventDropProps<Event>) {
    const overlappedEvents = getOverlappedEvents(args)
    const { start } = args

    if (!checkValidStartDate(start as Date)) return false

    // For simplicity, just undo the move.
    // if (overlappedEvents.length) return false

    try {
      // For a better solution, we must move it to the first accessible area
      const currentEventArgs = { ...args }
      if (overlappedEvents.length) {
        const lastOverlappedEvent = overlappedEvents[overlappedEvents.length - 1]

        const nextAccessibleArea = getNextAccessibleArea(
          lastOverlappedEvent.end as Date,
          currentEventArgs.event
        )

        if (!nextAccessibleArea) return false

        const { end: newEnd, start: newStart } = nextAccessibleArea
        currentEventArgs.start = newStart
        currentEventArgs.end = newEnd
      }

      resetHiddenGapsEvent()
      onEventDrop && onEventDrop(currentEventArgs)
    } catch (e) {
      LoggerUtils.error('handleEventDrop error', e)
    }
  }

  function handleEventResize(args: OnEventResizeProps<Event>) {
    try {
      const { start, end, event } = args

      if (!checkValidStartDate(start as Date)) return false

      const currentEventArgs = {
        ...args,
        end: DateUtils.adjustMinDuration(
          start as Date,
          end as Date,
          BigCalendarUtils.MINIMUM_EVENT_DURATION
        ),
      }
      const overlappedEvents = getOverlappedEvents(currentEventArgs)

      // For simplicity, just undo the move.
      // if (overlappedEvents.length) return false

      // For a better solution, we must change the corresponding event border
      if (overlappedEvents.length) {
        // "Start" moved
        if ((end as Date).getTime() === (event.end as Date).getTime()) {
          const lastOverlappedEvent = overlappedEvents[overlappedEvents.length - 1]
          currentEventArgs.start = DateUtils.addMinutes(
            lastOverlappedEvent.end as Date,
            MACHINE_TOTAL_REBOOT_DURATION
          )
        } else {
          // "End" moved
          const firstOverlappedEvent = overlappedEvents[0]
          currentEventArgs.end = DateUtils.subMinutes(
            firstOverlappedEvent.start as Date,
            MACHINE_TOTAL_REBOOT_DURATION
          )
        }
      }
      resetHiddenGapsEvent()

      // TODO: Check moving outside day borders, if it needed

      onEventResize && onEventResize(currentEventArgs)
    } catch (e) {
      LoggerUtils.error('handleEventResize error', e)
    }
  }

  function handleCreateEvent(args: OnSelectSlotProps) {
    try {
      const { start, end } = args

      if (!checkValidStartDate(start as Date)) return false

      const currentEventArgs = {
        ...args,
        end: DateUtils.adjustMinDuration(
          start as Date,
          end as Date,
          BigCalendarUtils.MINIMUM_EVENT_DURATION
        ),
      }
      const newEvent = {
        id: 'id',
        resourceId: `${args.resourceId}`,
        start: currentEventArgs.start as Date,
        end: currentEventArgs.end as Date,
        currentBooking: true,
      }
      const overlappedEvents = getOverlappedEvents({ ...currentEventArgs, event: newEvent })

      // For simplicity, just undo the move.
      // if (overlappedEvents.length) return false

      // For a better solution, we must move it to the first accessible area
      if (overlappedEvents.length) {
        const lastOverlappedEvent = overlappedEvents[overlappedEvents.length - 1]
        const nextAccessibleArea = getNextAccessibleArea(lastOverlappedEvent.end as Date, newEvent)

        if (!nextAccessibleArea) return false

        const { end: newEnd, start: newStart } = nextAccessibleArea
        currentEventArgs.start = newStart
        currentEventArgs.end = newEnd
      }

      onSelectSlot && onSelectSlot(currentEventArgs)
      // Delete hidden gaps on create
      resetHiddenGapsEvent()
    } catch (e) {
      LoggerUtils.error('handleCreateEvent error', e)
    }
  }

  const handleSelectEvent: OnSelectEventProps<Event> = (event, e) => {
    resetHiddenGapsEvent()
    if (onSelectEvent) onSelectEvent(event, e)
  }

  function renderRefreshButton() {
    if (!onRefresh) return null
    return (
      <>
        <Button
          size={Size.Small}
          icon='refresh'
          title={t(`${BigCalendarUtils.I18_PATH}.refreshCalendar`)}
          variant={ButtonVariant.OutlinedOceanLight}
          onClick={onRefresh}
        />
        <Spacer />
      </>
    )
  }

  return (
    <BigCalendarWrapper data-test={dataTest}>
      {renderRefreshButton()}
      <DragAndDropCalendar
        events={events}
        localizer={BigCalendarUtils.localizer}
        defaultView='day'
        showMultiDayTimes
        views={['day', 'week', 'month']}
        step={BigCalendarUtils.CALENDAR_STEP}
        timeslots={BigCalendarUtils.CALENDAR_TIMESLOTS}
        defaultDate={defaultDate}
        resources={resources}
        resourceIdAccessor={resourceIdAccessor}
        resourceTitleAccessor={resourceTitleAccessor}
        eventPropGetter={eventStyleGetter}
        formats={BigCalendarUtils.FORMATS}
        slotPropGetter={slotPropGetter}
        components={COMPONENTS}
        messages={BigCalendarUtils.getMessages()}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        selectable={selectable}
        onSelectSlot={handleCreateEvent}
        draggableAccessor={draggableAccessor}
        onView={onView}
        onSelectEvent={handleSelectEvent}
        getNow={getNow}
        onRangeChange={onRangeChange}
      />
    </BigCalendarWrapper>
  )
}

export default BigCalendar
